import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

export default function CustomerTerms() {
    return (
        <div className="min-h-screen bg-background">
            <header className="border-b sticky top-0 bg-background/95 backdrop-blur z-10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
                    <Button
                        onClick={() => window.history.back()}
                        variant="ghost"
                        size="icon"
                        data-testid="button-back"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <h1 className="text-lg font-semibold">Terms of Use</h1>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-8">
                <Card className="p-6 md:p-8">
                    <div className="prose prose-slate dark:prose-invert max-w-none">
                        <h1 className="text-3xl font-bold mb-2">Customer Terms of Use</h1>
                        <p className="text-muted-foreground mb-8">Last updated: January 1, 2026</p>

                        <p className="text-lg mb-6">
                            Welcome! By using this digital menu and AI waiter service powered by Waiterix,
                            you agree to the following terms.
                        </p>

                        {/* Section 1: AI Waiter Usage */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">1. AI Waiter (Lelah) Usage</h2>
                        <p className="mb-4">
                            When interacting with our AI waiter "Lelah," please be aware of the following:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                <strong>Microphone Access:</strong> You must grant microphone access to
                                communicate with Lelah. Your voice is transmitted in real-time to our
                                AI service providers for processing.
                            </li>
                            <li>
                                <strong>Audio Data:</strong> We do not permanently store audio recordings
                                beyond what is necessary for real-time conversation processing.
                            </li>
                            <li>
                                <strong>AI Limitations:</strong> Lelah may occasionally make mistakes,
                                misunderstand requests, or provide inaccurate information. Please verify
                                important details with restaurant staff.
                            </li>
                        </ul>

                        {/* Section 2: Food Allergies & Dietary Info */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">2. Food Allergies & Dietary Information</h2>
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4 mb-4">
                            <p className="text-amber-800 dark:text-amber-200 font-medium">
                                ⚠️ Important Allergy Notice
                            </p>
                            <p className="text-amber-700 dark:text-amber-300 text-sm mt-2">
                                While Lelah attempts to provide accurate allergen and dietary information,
                                this information should <strong>not be solely relied upon</strong> for
                                severe allergies or medical conditions. Always confirm with restaurant staff
                                before ordering.
                            </p>
                        </div>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                Allergen information may not be 100% accurate or complete.
                            </li>
                            <li>
                                Cross-contamination risks exist in restaurant kitchens.
                            </li>
                            <li>
                                For life-threatening allergies, speak directly with kitchen staff.
                            </li>
                        </ul>

                        {/* Section 3: Order Accuracy */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">3. Order Accuracy</h2>
                        <p className="mb-4">
                            You are responsible for reviewing your order before confirming. Please:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                Review all items in your cart before placing your order.
                            </li>
                            <li>
                                Verify special instructions and modifications are correct.
                            </li>
                            <li>
                                Check that quantities and sizes are accurate.
                            </li>
                            <li>
                                Confirm with restaurant staff if you have any concerns.
                            </li>
                        </ul>

                        {/* Section 4: Payment */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">4. Payment Processing</h2>
                        <p className="mb-4">
                            Payment processing is handled by secure third-party providers:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                Your payment information is encrypted and never stored on our servers.
                            </li>
                            <li>
                                Refunds and disputes are handled by the restaurant in accordance with
                                their refund policy.
                            </li>
                            <li>
                                Tips added to your order go directly to restaurant staff.
                            </li>
                        </ul>

                        {/* Section 5: Privacy */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">5. Privacy & Data</h2>
                        <p className="mb-4">
                            We respect your privacy. Here's how we handle your data:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                <strong>Voice Data:</strong> Transmitted for real-time AI processing only;
                                not permanently stored.
                            </li>
                            <li>
                                <strong>Order History:</strong> Stored securely to display your orders
                                on the restaurant's dashboard.
                            </li>
                            <li>
                                <strong>No Marketing:</strong> We do not use your data for marketing
                                purposes or sell it to third parties.
                            </li>
                        </ul>

                        {/* Section 6: Service Limitations */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">6. Service Limitations</h2>
                        <p className="mb-4">
                            Please understand that:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                The AI may not always be available due to technical issues.
                            </li>
                            <li>
                                Menu items shown may occasionally be unavailable.
                            </li>
                            <li>
                                Prices displayed are set by the restaurant and subject to change.
                            </li>
                            <li>
                                Service quality depends on your internet connection and device.
                            </li>
                        </ul>

                        {/* Section 7: Liability */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">7. Limitation of Liability</h2>
                        <p className="mb-4">
                            Waiterix provides this service "as is." We are not liable for:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>Errors or inaccuracies in AI responses</li>
                            <li>Issues with food quality or preparation (handled by restaurant)</li>
                            <li>Payment processing errors (handled by payment provider)</li>
                            <li>Service interruptions or technical difficulties</li>
                        </ul>

                        {/* Section 8: Contact */}
                        <h2 className="text-2xl font-bold mb-4 mt-8">8. Questions or Concerns</h2>
                        <p className="mb-4">
                            If you have any questions about these terms or your experience:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>
                                For food or service issues: Speak with restaurant staff
                            </li>
                            <li>
                                For technical issues with the AI: Contact{' '}
                                <a href="mailto:support@waiterix.com" className="text-primary underline">
                                    support@waiterix.com
                                </a>
                            </li>
                        </ul>

                        {/* Acceptance */}
                        <div className="bg-muted/50 rounded-lg p-4 mt-8">
                            <p className="text-sm text-muted-foreground">
                                By tapping "Start" on the welcome screen, you acknowledge that you have
                                read, understood, and agree to these terms of use.
                            </p>
                        </div>
                    </div>
                </Card>

                <div className="text-center mt-6">
                    <Button
                        onClick={() => window.history.back()}
                        size="lg"
                        data-testid="button-back-to-menu"
                    >
                        Back to Menu
                    </Button>
                </div>
            </main>

            <footer className="border-t py-6 mt-12">
                <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
                    <p>Powered by <strong>Waiterix</strong> • © 2026 All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
